import React, { useEffect, useMemo, useState } from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import Tooltip from '@material-ui/core/Tooltip'
import { NavLink, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { getRoutesBasedOnRole } from 'routes'
import { colors } from 'utils/colors'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import AdminTopBar, { TOPBAR_HEIGHT } from 'components/AdminTopBar'
import { LinearProgress } from '@material-ui/core'
import SecureRoute from 'components/SecureRoute'
import { useSelector } from 'react-redux'
import NotFound from 'components/NotFound'
import ForcePasswordChange from 'views/admin/ForcePasswordChange'

const SIDEBAR_WIDTH = 64
const SIDEBAR_WIDTH_EXPANDED = 220

const Template = () => {
  const history = useHistory()
  const { pathname } = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const classes = useStyles()
  const { user } = useSelector((state) => state.auth)
  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    if (!user) {
      history.replace('/')
    }
  }, [user])

  const routes = useMemo(() => {
    return getRoutesBasedOnRole(user?.role)
  }, [user?.role])

  if (!routes?.length) {
    return <LinearProgress color="secondary" />
  }

  if (user?.mustChangePassword) {
    return <ForcePasswordChange />
  }

  const visibleRoutes = routes.filter(
    (route) => route?.visible === undefined || route?.visible({ conference }) === true
  )

  return (
    <Box className={classes.root}>
      <AdminTopBar onToggleSidebar={() => setSidebarOpen((v) => !v)} />

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        className={sidebarOpen ? classes.drawer : classes.drawerClosed}
        classes={{
          paper: sidebarOpen ? classes.drawerPaper : classes.drawerPaperClosed
        }}>
        <List disablePadding className={classes.navList}>
          {visibleRoutes.map((route) => {
            const to = route.path.includes(':conferenceId')
              ? route.path.replace(':conferenceId', conference?.id || '')
              : route.path
            const isActive = pathname.includes(route.path)

            return (
              <Tooltip
                key={route.name}
                title={!sidebarOpen ? route.name : ''}
                placement="right"
                arrow>
                <NavLink to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <ListItem
                    button
                    disableRipple
                    className={`${classes.navItem} ${isActive ? classes.navItemActive : ''} ${
                      !sidebarOpen ? classes.navItemCollapsed : ''
                    }`}>
                    <route.icon
                      className={isActive ? classes.navIconActive : classes.navIcon}
                      style={{ fontSize: 22 }}
                    />
                    {sidebarOpen && (
                      <Typography
                        className={`${classes.navLabel} ${isActive ? classes.navLabelActive : ''}`}>
                        {route.name}
                      </Typography>
                    )}
                    {isActive && <Box className={classes.activeIndicator} />}
                  </ListItem>
                </NavLink>
              </Tooltip>
            )
          })}
        </List>
      </Drawer>

      {/* Main content */}
      <Box component="main" className={classes.content}>
        <Switch>
          {visibleRoutes.map((route) => (
            <SecureRoute path={route.path} exact={false} key={route.name}>
              <Box className={classes.pageWrapper}>{route.component}</Box>
            </SecureRoute>
          ))}
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Box>
    </Box>
  )
}

export default Template

const useStyles = makeStyles((theme) => ({
  '@global': {
    'html, body, #root': {
      height: '100% !important',
      overflow: 'hidden !important'
    }
  },
  root: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    backgroundColor: '#ffffff'
  },
  drawer: {
    width: SIDEBAR_WIDTH_EXPANDED,
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerClosed: {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  drawerPaper: {
    width: SIDEBAR_WIDTH_EXPANDED,
    top: TOPBAR_HEIGHT,
    height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
    backgroundColor: '#ffffff',
    borderRight: '1px solid #e2e8f0',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    '&::-webkit-scrollbar': {
      width: 4
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#e2e8f0',
      borderRadius: 4
    }
  },
  drawerPaperClosed: {
    width: SIDEBAR_WIDTH,
    top: TOPBAR_HEIGHT,
    height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
    backgroundColor: '#ffffff',
    borderRight: '1px solid #e2e8f0',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  navList: {
    paddingTop: 12,
    paddingBottom: 12
  },
  navItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '10px 12px',
    margin: '2px 8px',
    borderRadius: 10,
    minHeight: 44,
    gap: 10,
    transition: 'background-color 0.15s, transform 0.1s',
    '&:hover': {
      backgroundColor: '#f0f9ff',
      transform: 'translateX(2px)'
    }
  },
  navItemActive: {
    backgroundColor: '#e0f2fe !important',
    '&:hover': {
      backgroundColor: '#bae6fd !important'
    }
  },
  navItemCollapsed: {
    justifyContent: 'center',
    padding: '10px 0'
  },
  navIcon: {
    color: '#94a3b8',
    transition: 'color 0.15s'
  },
  navIconActive: {
    color: colors.indigo
  },
  navLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: '#64748b',
    textAlign: 'left',
    lineHeight: 1.2,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  navLabelActive: {
    color: colors.indigo,
    fontWeight: 700
  },
  activeIndicator: {
    position: 'absolute',
    left: -8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: 32,
    borderRadius: '0 3px 3px 0',
    backgroundColor: colors.indigo
  },
  content: {
    flexGrow: 1,
    marginTop: TOPBAR_HEIGHT,
    height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: theme.transitions.create('margin-left', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e1',
      borderRadius: 6
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#94a3b8'
    }
  },
  pageWrapper: {
    padding: '16px 24px',
    minHeight: '100%',
    backgroundColor: '#ffffff'
  }
}))
